<template>
  <b-modal
    ref="modal"
    :title="$t('error.general')"
    header-bg-variant="danger"
    header-text-variant="white"
    size="lg"
    scrollable
  >
    <b-alert
      v-if="errorSummary"
      variant="danger"
      show
    >
      {{ errorSummary }}
    </b-alert>

    <section
      v-for="(error, index) in errorDiagnostics"
      :key="index"
      class="modal-connector-error-section"
    >
      <div class="modal-connector-error-section-header">
        <strong>{{ error.title }}</strong>
        <b-badge variant="danger">
          {{ error.status }}
        </b-badge>
      </div>

      <div
        v-if="error.message"
        class="modal-connector-error-message"
      >
        {{ error.message }}
      </div>
      <div
        v-else
        class="modal-connector-error-no-message"
      >
        {{ $t("error.unknown") }}
      </div>

      <dl class="modal-connector-error-meta">
        <template v-if="error.method || error.url">
          <dt class="modal-connector-error-meta-title">{{ $t("label.request") }}</dt>
          <dd class="modal-connector-error-meta-content">
            <span v-if="error.method">{{ error.method }}</span>
            <code
              v-if="error.url"
              class="modal-connector-error-url"
            >
              {{ error.url }}
            </code>
          </dd>
        </template>

        <template v-if="error.details">
          <dt class="modal-connector-error-meta-title">{{ $t("label.technical_details") }}</dt>
          <dd class="modal-connector-error-meta-content">
            <pre class="modal-connector-error-details">{{ error.details }}</pre>
          </dd>
        </template>
      </dl>
    </section>

    <template #modal-footer="{ hide }">
      <b-button
        variant="outline-danger"
        @click="hide"
      >
        {{ $t("btn.close") }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
export default {
  name: "ModalConnectorError",
  props: {
    errorSummary: {
      type: String,
      default: "",
    },
    errors: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    errorDiagnostics() {
      return this.errors.map(({ title, error }) =>
        this.buildErrorDiagnostic(title, error)
      );
    },
  },
  methods: {
    show() {
      this.$refs.modal.show();
    },
    hide() {
      this.$refs.modal.hide();
    },
    buildErrorDiagnostic(title, error) {
      const response = error?.response;
      const config = error?.config || response?.config || {};
      const status = response?.status
        ? `${response.status} ${response.statusText || ""}`.trim()
        : this.$t("error.network");

      const result = {
        title,
        status,
        method: config.method?.toUpperCase(),
        url: (config.url || "").replace(/([?&](key|token)=)[^&]+/gi, "$1*****"), // Hide key/token
        message: error?.message ?? "",
        details: this.getErrorDetails(error),
      };

      return result;
    },
    getErrorDetails(error) {
      const data = error?.response?.data ?? error?.data;

      if (data) {
        return JSON.stringify(data, null, 2);
      }

      return error?.stack || null;
    },
  },
};
</script>

<style scoped>
.modal-connector-error-section {
  display: flex;
  flex-direction: column;
  gap: 0.75em;

  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.25rem;
  padding: 1rem;
}
.modal-connector-error-section:not(:last-child) {
  margin-bottom: 0.75rem;
}
.modal-connector-error-section-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
}

.modal-connector-error-message,
.modal-connector-error-no-message {
  font-weight: 600;
}
.modal-connector-error-message {
  color: #721c24;
}

.modal-connector-error-meta {
  margin-bottom: 0;
}
.modal-connector-error-meta-title {
  color: grey;
  font-size: 0.8rem;
  text-transform: uppercase;
}
.modal-connector-error-meta-title:not(:first-child) {
  margin-top: 0.75rem;
}
.modal-connector-error-meta-content {
  margin-bottom: 0;
  margin-left: 0.5rem;
}

.modal-connector-error-url {
  display: block;
  word-break: break-all;
}

.modal-connector-error-details {
  max-height: 200px;
  overflow: auto;

  margin-bottom: 0;
  padding: 0.75rem;

  background-color: #f8f9fa;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0.25rem;

  white-space: pre-wrap;
  word-break: break-word;
}
</style>
